import { produce, Draft } from 'immer'
import { TActionTypes, IAction } from '@Root/types'
import getFullName from '@Utils/GetFullName'
import map from 'lodash/map'
import filter from 'lodash/filter'
import each from 'lodash/each'
import findIndex from 'lodash/findIndex'
import without from 'lodash/without'

import {
  IAccountUserState,
  AccountUserActionTypes,
  TAccountUserActionTypes,
  IFetchAccountUsersSuccessResponseBody,
  IDeleteAccountUsersSuccessPayload,
  IChangeSelectedAccountUsersPayload,
} from './types'

import {
  WorkspaceUserActionTypes,
  IWorkspaceUser,
  IDeleteWorkspaceUserSuccessPayload,
} from '@SiteModules/WorkspaceUser/types'

import {
  TWorkspaceActionTypes,
  WorkspaceActionTypes,
  IDeleteWorkspacesSuccessPayload,
  ICreateWorkspaceSuccessResponseBody,
} from '@SiteModules/Workspace/types'

import {
  TUserActionTypes,
  UserActionTypes,
  IUpdateProfileSuccessPayload,
  IChangeAvatarSuccessPayload,
} from '@SiteModules/User/types'

export const initialState: IAccountUserState = {
  accountUsers: [],
  selectedAccountUserIds: [],
  hasLoadedInitially: false,
  loading: false,
}

type TAction = TActionTypes | TAccountUserActionTypes | TWorkspaceActionTypes | TUserActionTypes

export const accountUsersReducer = (
  state: IAccountUserState = initialState,
  action: TAction
): IAccountUserState => {
  return produce(state, draft => {
    switch (action.type) {
      case AccountUserActionTypes.FETCH_ACCOUNT_USERS:
        fetchAccountUsers(draft)
        break

      case AccountUserActionTypes.FETCH_ACCOUNT_USERS_SUCCESS:
        fetchAccountUsersSuccess(draft, action)
        break

      case AccountUserActionTypes.FETCH_ACCOUNT_USERS_FAILURE:
        fetchAccountUsersFailure(draft)
        break

      case AccountUserActionTypes.RESET_ACCOUNT_USERS:
        resetAccountUsers(draft)
        break

      case AccountUserActionTypes.DELETE_ACCOUNT_USERS_SUCCESS:
        deleteAccountUsersSuccess(draft, action)
        break

      case AccountUserActionTypes.CHANGE_SELECTED_ACCOUNT_USERS:
        changeSelectedAccountUsers(draft, action)
        break

      case AccountUserActionTypes.UNSELECT_ACCOUNT_USERS:
        unselectAccountUsers(draft)
        break

      case AccountUserActionTypes.SELECT_ALL_ACCOUNT_USERS:
        selectAllAccountUsers(draft)
        break

      case WorkspaceActionTypes.DELETE_WORKSPACES_SUCCESS:
        deleteWorkspaceUsers(draft, action)
        break

      case WorkspaceActionTypes.CREATE_WORKSPACE_SUCCESS:
        createAdminWorkspaceUsers(draft, action)
        break

      case WorkspaceUserActionTypes.DELETE_WORKSPACE_USER_SUCCESS:
        deleteWorkspaceUserSuccess(draft, action)
        break

      case UserActionTypes.UPDATE_PROFILE_SUCCESS:
        updateAccountUserAfterUpdateUserProfile(draft, action)
        break

      case UserActionTypes.CHANGE_AVATAR_SUCCESS:
        updateAccountUserAfterChangeUserAvatar(draft, action)
        break

      default:
        break
    }
  })
}

const fetchAccountUsers = (draft: Draft<IAccountUserState>) => {
  draft.loading = true
}

function isIFetchAccountUsersSuccessAction(arg: any): arg is IAction<IFetchAccountUsersSuccessResponseBody> {
  return arg.type === AccountUserActionTypes.FETCH_ACCOUNT_USERS_SUCCESS
}

const fetchAccountUsersSuccess = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIFetchAccountUsersSuccessAction(action)) { return }

  const loadedAccountUsers = map(action.payload.data, accountUserData => {
    const accountUserWorkspaceUserIds = map(accountUserData.relationships.workspaceUsers.data, workspaceUser => workspaceUser.id)

    let accountUserWorkspaceUsers: IWorkspaceUser[] = []

    if (accountUserWorkspaceUserIds.length > 0 && action.payload.included.length > 0) {
      const accountUserWorkspaceUsersData = filter(action.payload.included, includedAccountUserWorkspaceUsersData => accountUserWorkspaceUserIds.includes(includedAccountUserWorkspaceUsersData.id))
      accountUserWorkspaceUsers = map(accountUserWorkspaceUsersData, accountUserWorkspaceUserData => {
        const parsedId = parseInt(accountUserWorkspaceUserData.id, 10)
        const workspaceId = Number.isNaN(parsedId) ? -1 : parsedId

        return (
          { id: workspaceId, attributes: accountUserWorkspaceUserData.attributes }
        )
      })
    }

    return ({ id: parseInt(accountUserData.id, 10), workspaceUsers: accountUserWorkspaceUsers, attributes: accountUserData.attributes })
  })

  draft.accountUsers = loadedAccountUsers
  draft.hasLoadedInitially = true
  draft.loading = false
}

const fetchAccountUsersFailure = (draft: Draft<IAccountUserState>) => {
  draft.loading = false
}

const resetAccountUsers = (draft: Draft<IAccountUserState>) => {
  draft.accountUsers = []
  draft.selectedAccountUserIds = []
  draft.hasLoadedInitially = false
}

function isIDeleteAccountUsersSuccessAction(arg: any): arg is IAction<IDeleteAccountUsersSuccessPayload> {
  return arg.type === AccountUserActionTypes.DELETE_ACCOUNT_USERS_SUCCESS
}

const deleteAccountUsersSuccess = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIDeleteAccountUsersSuccessAction(action)) { return }

  draft.accountUsers = filter(draft.accountUsers, accountUser => !action.payload.accountUserIds.includes(accountUser.id))
  draft.selectedAccountUserIds = []
}

function isIChangeSelectedAccountUsersAction(arg: any): arg is IAction<IChangeSelectedAccountUsersPayload> {
  return arg.type === AccountUserActionTypes.CHANGE_SELECTED_ACCOUNT_USERS
}

const changeSelectedAccountUsers = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIChangeSelectedAccountUsersAction(action)) { return }

  const { selectAccountUser, accountUserId } = action.payload

  if (selectAccountUser) {
    draft.selectedAccountUserIds.push(accountUserId)
  } else {
    draft.selectedAccountUserIds = without(draft.selectedAccountUserIds, accountUserId)
  }
}

const unselectAccountUsers = (draft: Draft<IAccountUserState>) => {
  draft.selectedAccountUserIds = []
}

const selectAllAccountUsers = (draft: Draft<IAccountUserState>) => {
  draft.selectedAccountUserIds = map(draft.accountUsers, accountUser => accountUser.id)
}

function isIDeleteWorkspacesSuccessAction(arg: any): arg is IAction<IDeleteWorkspacesSuccessPayload> {
  return arg.type === WorkspaceActionTypes.DELETE_WORKSPACES_SUCCESS
}

const deleteWorkspaceUsers = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIDeleteWorkspacesSuccessAction(action)) { return }

  draft.accountUsers = map(draft.accountUsers, accountUser => {
    const updatedWorkspaceUsers = filter(accountUser.workspaceUsers, workspaceUser => !action.payload.workspaceIds.includes(workspaceUser.attributes.workspaceId))
    return { ...accountUser, workspaceUsers: updatedWorkspaceUsers }
  })
}

function isICreateWorkspaceSuccessAction(arg: any): arg is IAction<ICreateWorkspaceSuccessResponseBody> {
  return arg.type === WorkspaceActionTypes.CREATE_WORKSPACE_SUCCESS
}

const createAdminWorkspaceUsers = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isICreateWorkspaceSuccessAction(action)) { return }

  const workspaceId = parseInt(action.payload.data.id, 10)
  const workspaceName = action.payload.data.attributes.name

  each(draft.accountUsers, accountUser => {
    if (accountUser.attributes.role === 'admin') {
      accountUser.workspaceUsers.push({ id: -1, attributes: { accessType: 'account_admin', accountUserId: accountUser.id, workspaceId, workspaceName } })
    }
  })
}

function isIDeleteWorkspaceUserSuccessAction(arg: any): arg is IAction<IDeleteWorkspaceUserSuccessPayload> {
  return arg.type === WorkspaceUserActionTypes.DELETE_WORKSPACE_USER_SUCCESS
}

const deleteWorkspaceUserSuccess = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIDeleteWorkspaceUserSuccessAction(action)) { return }

  draft.accountUsers = map(draft.accountUsers, accountUser => {
    const updatedWorkspaceUsers = filter(accountUser.workspaceUsers, workspaceUser => workspaceUser.id !== action.payload.workspaceUserId)
    return { ...accountUser, workspaceUsers: updatedWorkspaceUsers }
  })
}

function isIUpdateProfileSuccessAction(arg: any): arg is IAction<IUpdateProfileSuccessPayload> {
  return arg.type === UserActionTypes.UPDATE_PROFILE_SUCCESS
}

const updateAccountUserAfterUpdateUserProfile = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIUpdateProfileSuccessAction(action)) { return }

  const accountUsers = draft.accountUsers
  const accountUserToUpdateIndex = findIndex(accountUsers, accountUser => accountUser.attributes.userId === action.payload.userId)

  if (accountUserToUpdateIndex >= 0) {
    if (typeof(action.payload.lastName) === 'string' || typeof(action.payload.name) === 'string') {

      draft.accountUsers[accountUserToUpdateIndex].attributes.name = getFullName(action.payload.name, action.payload.lastName)
    }

    if (typeof(action.payload.email) === 'string') {
      draft.accountUsers[accountUserToUpdateIndex].attributes.email = action.payload.email
    }
  }
}

function isIChangeAvatarSuccessAction(arg: any): arg is IAction<IChangeAvatarSuccessPayload> {
  return arg.type === UserActionTypes.CHANGE_AVATAR_SUCCESS
}

const updateAccountUserAfterChangeUserAvatar = (draft: Draft<IAccountUserState>, action: TAction) => {
  if (!isIChangeAvatarSuccessAction(action)) { return }

  const accountUsers = draft.accountUsers
  const accountUserToUpdateIndex = findIndex(accountUsers, accountUser => accountUser.attributes.userId === action.payload.userId)

  if (accountUserToUpdateIndex >= 0) {
    draft.accountUsers[accountUserToUpdateIndex].attributes.avatarUrl = action.payload.url
  }
}
